import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { User, LayoutDashboard, ShieldCheck, Gift, Heart, Menu, X } from "lucide-react";
import { LogoFull } from "@/components/Logo";
import { NotificationBell } from "@/components/NotificationBell";
import { DesktopNav } from "@/components/header/DesktopNav";
import { MobileMenu } from "@/components/header/MobileMenu";
import { useAuth } from "@/context/AuthContext";
import { useFlags } from "@/hooks/useFlags";
import { useFavorites } from "@/hooks/useFavorites";
import { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";

const LANGS = [{ c: "fr", l: "FR" }, { c: "en", l: "EN" }, { c: "es", l: "ES" }, { c: "de", l: "DE" }];

export function Header() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const flags = useFlags();
  const { favs } = useFavorites();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  const dashLink = user && user.role === "loueur" ? "/loueur"
    : user && user.role === "partner" ? "/partenaire"
    : user && (user.role === "superadmin" || user.role === "sous-admin") ? "/admin" : "/my/bookings";

  const links = [
    { to: "/", label: t("nav.home") },
    { to: "/search", label: t("nav.vehicles") },
    ...(flags.v3_enabled ? [{ to: "/activites", label: t("activities.nav") }] : []),
    ...(flags.v2_enabled ? [{ to: "/tarifs", label: t("nav.pricing") }] : []),
    { to: "/about", label: t("nav.about") },
    { to: "/faq", label: t("nav.faq") },
    { to: "/contact", label: t("nav.contact") },
  ];

  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-white/90 border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="header-logo-link"><LogoFull /></Link>

        <DesktopNav links={links} pathname={loc.pathname} />

        <div className="flex items-center gap-2">
          <div className="hidden sm:flex items-center gap-0.5 bg-sand rounded-full p-1">
            {LANGS.map((lg) => (
              <button key={lg.c} data-testid={`lang-${lg.c}-button`} onClick={() => i18n.changeLanguage(lg.c)}
                className={`px-2 py-1 rounded-full text-xs font-bold transition-all ${i18n.language === lg.c ? "bg-canard text-white" : "text-muted2 hover:text-canard"}`}>
                {lg.l}
              </button>
            ))}
          </div>

          {flags.v3_enabled && (
            <button data-testid="header-fav-count" onClick={() => nav("/mes-favoris")} aria-label="favorites" className="relative hidden sm:flex items-center justify-center w-9 h-9 rounded-full hover:bg-sand transition-colors">
              <Heart className={`w-5 h-5 ${favs.length ? "fill-red-500 text-red-500" : "text-muted2"}`} />
              {favs.length > 0 && <span data-testid="header-fav-badge" className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{favs.length}</span>}
            </button>
          )}

          {user ? (
            <div className="hidden lg:flex items-center gap-2">
              {user && <NotificationBell />}
              <Button variant="ghost" size="sm" data-testid="header-dashboard-button" onClick={() => nav(dashLink)} className="rounded-full font-semibold">
                {user.role === "loueur" ? <LayoutDashboard className="w-4 h-4 mr-1" /> :
                 (user.role === "superadmin" || user.role === "sous-admin") ? <ShieldCheck className="w-4 h-4 mr-1" /> : <User className="w-4 h-4 mr-1" />}
                {user.role === "loueur" ? t("nav.dashboard") : user.role === "partner" ? t("nav.partner") : (user.role === "superadmin" || user.role === "sous-admin") ? t("nav.admin") : t("nav.myBookings")}
              </Button>
              <Button variant="ghost" size="sm" data-testid="header-profile-button" onClick={() => nav("/profile")} className="rounded-full font-semibold">
                {user.avatar_url ? <img src={fileUrl(user.avatar_url)} alt="" className="w-6 h-6 rounded-full object-cover mr-1" /> : <User className="w-4 h-4 mr-1" />} {t("nav.profile")}
              </Button>
              {(user.role === "loueur" || user.role === "client") && (
                <Button variant="ghost" size="sm" data-testid="header-rewards-button" onClick={() => nav(user.role === "loueur" ? "/loueur" : "/rewards")} className="rounded-full font-semibold">
                  <Gift className="w-4 h-4 mr-1 text-canard" /> {t("rewards.title")}
                </Button>
              )}
              <Button size="sm" data-testid="header-logout-button" onClick={async () => { await logout(); nav("/"); }} className="rounded-full bg-canard hover:bg-canard-dark">
                {t("nav.logout")}
              </Button>
            </div>
          ) : (
            <div className="hidden lg:flex items-center gap-2">
              <Button variant="ghost" size="sm" data-testid="header-loueur-button" onClick={() => nav("/register?role=loueur")} className="rounded-full font-semibold text-canard">
                {t("nav.loueur")}
              </Button>
              <Button size="sm" data-testid="header-login-button" onClick={() => nav("/login")} className="rounded-full bg-canard hover:bg-canard-dark">
                {t("nav.login")}
              </Button>
            </div>
          )}

          <button className="lg:hidden p-2" data-testid="mobile-menu-button" onClick={() => setOpen(!open)}>
            {open ? <X className="w-6 h-6 text-ink" /> : <Menu className="w-6 h-6 text-ink" />}
          </button>
        </div>
      </div>

      <MobileMenu
        open={open}
        links={links}
        user={user}
        favs={favs}
        flagsV3={flags.v3_enabled}
        dashLink={dashLink}
        onClose={() => setOpen(false)}
        onNav={nav}
        onLogout={async () => { setOpen(false); await logout(); nav("/"); }}
      />
    </header>
  );
}
